import { lazy, Suspense, useEffect } from "react";
import { Outlet, Navigate, useRoutes, useLocation } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';
import { reportRoutes } from "./report-sections";

// Lazy load public pages too - users typically visit one at a time
const LoginPage = lazy(() => import("../pages/public/Login"));

// Context imports
import { useAuth } from "../context/AuthContext";
import { useMember, MemberProvider } from "../context/UserContext";
import { PermissionsProvider, usePermissions } from "../context/PermissionsContext";
import RCSMessageDialog from "../components/RCSMessageDialog/RCSMessageDialog";
import RequirePermission from "../components/RequirePermission/RequirePermission";
import RequirePlan from "../components/RequirePlan/RequirePlan";
import { ChapterPlan } from "../types/membership";

// Lazy load all private components and dashboard layout
const DashboardLayout = lazy(() => import("../layouts/dashboard"));
const UserSettings = lazy(() => import("../pages/UserSettings"));
const BudgetsPage = lazy(() => import("../pages/private/Budgets/BudgetsPage"));
const LedgersPage = lazy(() => import("../pages/private/Ledgers/LedgersPage"));
const BudgetingPage = lazy(() => import("../pages/private/Budgeting/BudgetingPage"));
const LedgerCategoriesPage = lazy(() => import("../pages/private/Ledgers/IndividualLedgerCategoryPage"));
const ExternalTransactionsPage = lazy(() => import("../pages/ExternalTransactionsPage"));
const IndividualLedgerPage = lazy(() => import("../pages/private/Ledgers/IndividualLedgerPage"));
const IndividualBudgetsPage = lazy(() => import("../pages/private/Budgets/IndividualBudgetsPage"));
const ReimbursementsPage = lazy(() => import("../pages/private/Reimbursements/ReimbursementsPage"));
const UserChargePage = lazy(() => import("../pages/private/Charges/UserChargePage"));
const ContractsPage = lazy(() => import("../pages/private/Contracts/ContractsPage"));
const TemplateBuilderPage = lazy(() => import("../pages/private/Contracts/TemplateBuilderPage"));
const IndividualChargeTypePage = lazy(() => import("../pages/private/Charges/IndividualChargeTypePage"));
const ChargeTypesPage = lazy(() => import("../pages/private/Charges/ChargeTypesPage"));
const ComplianceIndividualPage = lazy(() => import("../pages/private/Compliance/CompliancePage"));
const BalancesDashboard = lazy(() => import("../components/BalancesDashboard/BalancesDashboard"));
const PaymentPlansPage = lazy(() => import("../pages/private/PaymentPlans/PaymentPlansPage"));
const MembershipRequestsPage = lazy(() => import("../pages/private/Requests/MembershipRequestsPage"));
const TasksPage = lazy(() => import("../pages/private/Tasks/TasksPage"));
const AccountantOverviewPage = lazy(() => import("../pages/private/Accountant/AccountantOverviewPage"));
const ChapterDashboardPage = lazy(() => import("../pages/private/Accountant/ChapterDashboardPage"));

export const IndexPage = lazy(() => import("../pages/Dashboard"));
export const MembersPage = lazy(() => import("../pages/private/Members/MembersPage"));
export const MemberPage = lazy(() => import("../pages/private/Members/IndividualMemberPage"));
export const ImportMembersPage = lazy(() => import("../pages/private/Members/ImportMembersPage"));
export const ImportCustomFieldsPage = lazy(() => import("../pages/private/Members/ImportCustomFieldsPage"));
export const GroupsPage = lazy(() => import("../pages/private/Groups/GroupsPage"));
export const GroupPage = lazy(() => import("../pages/private/Groups/GroupSinglePage"));
export const BalancePage = lazy(() => import("../pages/BalancePage"));
export const TransactionsPage = lazy(() => import("../pages/private/Transactions/TransactionsPage"));

// Preload functions for critical pages
export const preloadBalancePage = () => import("../pages/BalancePage");
export const preloadMembersPage = () => import("../pages/private/Members/MembersPage");
export const ChapterSettings = lazy(() => import("../pages/ChapterSettings"));
export const ChargesPage = lazy(() => import("../pages/private/Balances/BalancesPage"));
export const RegistrationSuccess = lazy(() => import("../pages/public/RegistrationSuccess"));
export const RegisterOrganization = lazy(() => import("../pages/public/RegisterOrganization"));
export const ForgotPassword = lazy(() => import("../pages/public/ForgotPassword"));
export const ResetPassword = lazy(() => import("../pages/public/ResetPassword"));
export const UserInvitePage = lazy(() => import("../pages/public/UserInvitePage"));
// MYG-122: public pre-onboarding Crowded bank application (link carries the Crowded id).
export const PublicBankApplicationPage = lazy(() => import("../pages/public/PublicBankApplicationPage"));
export const LogoutPage = lazy(() => import("../pages/public/LogoutPage"));
export const AnnouncementsPage = lazy(() => import("../pages/private/Communications/AnnouncementsPage"));
const FirstLoginPage = lazy(() => import("../pages/private/FirstLoginPage"));
const StorePage = lazy(() => import("../pages/private/Store/StorePage"));
const IndividualProductPage = lazy(() => import("../pages/private/Store/IndividualProductPage"));
const SupportersPage = lazy(() => import("../pages/private/Supporters/SupportersPage"));
const IndividualSupporterGroupPage = lazy(() => import("../pages/private/Supporters/IndividualSupporterGroupPage"));
const IndividualTierPage = lazy(() => import("../pages/private/Supporters/IndividualTierPage"));
const BankingPage = lazy(() => import("../pages/private/Banking/BankingPage"));
const IndividualBankAccountPage = lazy(() => import("../pages/private/Banking/IndividualBankAccountPage"));
const IndividualCardPage = lazy(() => import("../pages/private/Cards/IndividualCardPage"));
const GCMImportPage = lazy(() => import("../pages/private/GCMImportPage"));

// Loading component with progress bar and torch icon
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column',
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    gap: '24px'
  }}>
    <img 
      src="/torch.svg" 
      alt="Loading" 
      style={{ 
        width: '64px', 
        height: '64px',
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
      }} 
    />
    <div style={{
      width: '200px',
      height: '4px',
      backgroundColor: '#e0e0e0',
      borderRadius: '2px',
      overflow: 'hidden'
    }}>
      <div style={{
        height: '100%',
        backgroundColor: '#46bbc9',
        borderRadius: '2px',
        animation: 'progress 1.5s ease-in-out infinite'
      }} />
    </div>
    <style>
      {`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes progress {
          0% { width: 0%; transform: translateX(0); }
          50% { width: 100%; transform: translateX(0); }
          100% { width: 100%; transform: translateX(100%); }
        }
      `}
    </style>
  </div>
);

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { session, sessionLoading, signOut } = useAuth();
  const location = useLocation();

  if (sessionLoading) {
    return <LoadingFallback />;
  }

  if (!session) {
    // Redirect to login with the current location in state
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Block external_payer and hq users by checking JWT user_type
  try {
    const decoded = jwtDecode<{ user_type?: string }>(session.access_token);
    if (decoded.user_type === 'external_payer' || decoded.user_type === 'hq') {
      // Backstop for cached/deep-linked blocked sessions — keep them off protected
      // routes. Login's session effect shows the messaging + member-code flow (MYG-102).
      signOut();
      return <Navigate to="/login" replace />;
    }
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    // If JWT decode fails, fall through to let other auth checks handle it
  }

  // Wrap private routes with MemberProvider and PermissionsProvider
  return (
    <MemberProvider>
      <PermissionsProvider>
        <PrivateRouteContent>{children}</PrivateRouteContent>
      </PermissionsProvider>
    </MemberProvider>
  );
}

function PrivateRouteContent({ children }: { children: React.ReactNode }) {
  const { member, loading: memberLoading, switching } = useMember();
  const location = useLocation();

  // `switching` covers the switch-chapter edge fn + refreshSession window before the
  // full reload — show the loader so the previous chapter's screen never flashes.
  if (memberLoading || switching) {
    return <LoadingFallback />;
  }

  if (!member) {
    // Redirect to login if member data not found
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (member && !member.onboarding_finalized) {
    return <Navigate to="/first-login" replace />;
  }

  return (
    <>
      <RCSMessageDialog />
      {children}
    </>
  );
}

function FirstLoginRoute({ children }: { children: React.ReactNode }) {
  const { session, sessionLoading } = useAuth();
  const location = useLocation();

  if (sessionLoading) {
    return <LoadingFallback />;
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <MemberProvider>
      <FirstLoginRouteContent>{children}</FirstLoginRouteContent>
    </MemberProvider>
  );
}

function FirstLoginRouteContent({ children }: { children: React.ReactNode }) {
  const { member, loading: memberLoading } = useMember();

  if (memberLoading) {
    return <LoadingFallback />;
  }

  if (!member) {
    return <Navigate to="/login" replace />;
  }

  // If already onboarded, redirect to home
  if (member.onboarding_finalized) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Helper to wrap route elements with permission checks (and optionally a
// minimum chapter plan tier)
const guard = (
  element: React.ReactNode,
  opts: { permissions?: string[]; requireAdmin?: boolean; requirePlan?: ChapterPlan }
) => {
  const { requirePlan, ...permissionOpts } = opts;
  return (
    <RequirePermission {...permissionOpts}>
      {requirePlan ? <RequirePlan plan={requirePlan}>{element}</RequirePlan> : element}
    </RequirePermission>
  );
};

// MYG-116: "/" is role-based. Accountants have a stateful home (firm overview table in
// 'overview' mode — side nav hidden by the layout — vs the chapter dashboard once they
// open a chapter; mode persists in localStorage). Any chapter admin lands on the chapter
// dashboard; non-admin members keep their personal balance page.
function IndexLanding() {
  const { isAccountant, accountantViewMode, accountantLoading } = useMember();
  const { isAdmin, loading: permissionsLoading } = usePermissions();

  // Wait for accountant detection before deciding "/", else an accountant (who is an
  // alumni-admin ⇒ isAdmin) briefly renders the chapter dashboard — firing its query
  // burst — before snapping to the overview.
  if (accountantLoading) {
    return <LoadingFallback />;
  }
  if (isAccountant && accountantViewMode === "overview") {
    return <AccountantOverviewPage />;
  }
  // Wait for permissions so admins don't briefly flash the personal balance page.
  if (permissionsLoading) {
    return <LoadingFallback />;
  }
  return isAdmin ? <ChapterDashboardPage /> : <BalancePage />;
}

// Reset window scroll to the top on every route change (the app scrolls the window,
// and React Router doesn't restore scroll — long pages like the dashboard would
// otherwise keep their scroll position when navigating away).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function Router() {
  const routes = useRoutes([
    {
      element: (
        <PrivateRoute>
          <Suspense fallback={null}>
            <DashboardLayout>
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </DashboardLayout>
          </Suspense>
        </PrivateRoute>
      ),
      children: [
        // Unguarded routes - accessible to all authenticated users
        { element: <IndexLanding />, index: true },
        // MYG-116: admins land on the chapter dashboard at "/", so their personal
        // balance page moves here (reachable via the "my balance" nav item).
        { path: "my-balance", element: <BalancePage /> },
        { path: "members", element: <MembersPage /> },
        { path: "members/import", element: guard(<ImportMembersPage />, { permissions: ["CreateMember"] }) },
        { path: "members/import-fields", element: guard(<ImportCustomFieldsPage />, { permissions: ["UpdateMember"] }) },
        { path: "members/:memberId", element: <MemberPage /> },
        { path: "settings", element: <UserSettings /> },
        { path: "chapter", element: <ChapterSettings /> },
        { path: "charge/:chargeId", element: <UserChargePage /> },
        { path: "contracts", element: guard(<ContractsPage />, { requirePlan: 'plus' }) },
        { path: "contracts/templates/new", element: guard(<TemplateBuilderPage />, { requireAdmin: true, requirePlan: 'plus' }) },
        { path: "contracts/templates/:docusealTemplateId/edit", element: guard(<TemplateBuilderPage />, { requireAdmin: true, requirePlan: 'plus' }) },
        { path: "charge-types/:chargeId", element: <IndividualChargeTypePage /> },
        { path: "charge-types", element: <ChargeTypesPage /> },

        // Budget/ledger routes - data-driven access via hasFinancialAccess hook
        { path: "budgeting", element: <BudgetingPage /> },
        { path: "budgets", element: <BudgetsPage /> },
        { path: "budgets/:budgetId", element: <IndividualBudgetsPage /> },
        { path: "ledgers", element: <LedgersPage /> },
        { path: "ledgers/:ledgerId", element: <IndividualLedgerPage /> },
        { path: "ledger-category/:categoryId", element: <LedgerCategoriesPage /> },

        // Permission-guarded routes
        { path: "requests", element: guard(<MembershipRequestsPage />, { permissions: ["CreateMember"] }) },
        { path: "groups", element: guard(<GroupsPage />, { permissions: ["ReadGroups", "ManageGroups", "ManageGroupMembership", "ReadGroupMembership"] }) },
        { path: "groups/:groupId", element: guard(<GroupPage />, { permissions: ["ReadGroups", "ManageGroups", "ManageGroupMembership", "ReadGroupMembership"] }) },
        { path: "balances", element: guard(<BalancesDashboard />, { permissions: ["ReadTransaction", "CreateTransaction"] }) },
        { path: "payments", element: guard(<TransactionsPage />, { permissions: ["ReadTransaction", "CreateTransaction"] }) },
        { path: "plans", element: guard(<PaymentPlansPage />, { permissions: ["ViewPaymentPlans", "ManagePaymentPlans", "ViewPaymentPlanTemplates", "ManagePaymentPlanTemplates"] }) },
        { path: "expenses", element: guard(<ExternalTransactionsPage />, { permissions: ["ReadAccountingTransactions", "ManagePlaidAccounts", "ReadPlaidAccounts"] }) },
        { path: "reimbursements", element: guard(<ReimbursementsPage />, { permissions: ["ReadReimbursements"] }) },
        { path: "announcements", element: guard(<AnnouncementsPage />, { permissions: ["ReadAnnouncement", "CreateCommunication"] }) },
        { path: "compliance", element: guard(<ComplianceIndividualPage />, { permissions: ["ReadCompliance"] }) },

        // Admin-only routes
        { path: "tasks", element: guard(<TasksPage />, { requireAdmin: true }) },
        { path: "store", element: guard(<StorePage />, { requireAdmin: true }) },
        { path: "store/:productId", element: guard(<IndividualProductPage />, { requireAdmin: true }) },

        // Supporters routes
        { path: "supporters", element: guard(<SupportersPage />, { permissions: ["ManageSupporterGroups", "ViewSupporterGroups"] }) },
        { path: "supporters/:groupId", element: guard(<IndividualSupporterGroupPage />, { permissions: ["ManageSupporterGroups", "ViewSupporterGroups"] }) },
        { path: "supporters/:groupId/tiers/:tierId", element: guard(<IndividualTierPage />, { permissions: ["ManageSupporterGroups", "ViewSupporterGroups"] }) },
        { path: "gcm-import", element: guard(<GCMImportPage />, { requireAdmin: true }) },

        // Banking routes - admin only
        { path: "banking", element: guard(<BankingPage />, { requireAdmin: true }) },
        { path: "banking/:accountId", element: guard(<IndividualBankAccountPage />, { requireAdmin: true }) },
        { path: "cards/:cardId", element: guard(<IndividualCardPage />, { requireAdmin: true }) },

        // Report routes (guarded in report-sections.tsx)
        ...reportRoutes,
      ],
    },
    {
      path: "login",
      element: (
        <Suspense fallback={null}>
          <LoginPage />
        </Suspense>
      ),
    },
    {
      path: "logout",
      element: (
        <Suspense fallback={null}>
          <LogoutPage />
        </Suspense>
      ),
    },
    {
      path: "registration",
      element: (
        <Suspense fallback={null}>
          <RegisterOrganization />
        </Suspense>
      ),
    },
    {
      path: "registration-success",
      element: (
        <Suspense fallback={null}>
          <RegistrationSuccess />
        </Suspense>
      ),
    },
    {
      path: "forgot-password",
      element: (
        <Suspense fallback={null}>
          <ForgotPassword />
        </Suspense>
      ),
    },
    {
      path: "reset-password",
      element: (
        <Suspense fallback={null}>
          <ResetPassword />
        </Suspense>
      ),
    },
    {
      path: "invite/:onboarding_id",
      element: (
        <Suspense fallback={null}>
          <UserInvitePage />
        </Suspense>
      ),
    },
    {
      // MYG-122: public — no auth; distinct from the private "banking" routes.
      path: "bank-application/:crowdedChapterId",
      element: (
        <Suspense fallback={null}>
          <PublicBankApplicationPage />
        </Suspense>
      ),
    },
    {
      path: "first-login",
      element: (
        <FirstLoginRoute>
          <Suspense fallback={null}>
            <FirstLoginPage />
          </Suspense>
        </FirstLoginRoute>
      ),
    },
    {
      path: "*",
      element: <Navigate to="/login" replace />,
    },
  ]);

  return (
    <>
      <ScrollToTop />
      {routes}
    </>
  );
}
